import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { AppTask, ForecastChangedProduct, isGroupedTaskMetadata } from '@domain/tasks/models/Task';
import { APP_CONFIG } from '../../../../../config/app.config';
import ForecastCompareChart from './ForecastCompareChart';
import ForecastChangedMetrics from './ForecastChangedMetrics';
import { Icon, IconSize } from '@shared/icons';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import ProductCard from './ProductCard';

interface ForecastChangedDialogProps {
  open: boolean;
  task: AppTask | null;
  isAcknowledging?: boolean;
  onClose: () => void;
  onAcknowledge: (taskId: string) => Promise<void> | void;
  onAllChecked?: () => void;
}

const ForecastChangedDialog: React.FC<ForecastChangedDialogProps> = ({
  open,
  task,
  isAcknowledging = false,
  onClose,
  onAcknowledge,
  onAllChecked,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const products: ForecastChangedProduct[] =
    task && isGroupedTaskMetadata(task.metadata)
      ? (task.metadata.products as ForecastChangedProduct[])
      : task?.metadata && 'product' in task.metadata && task.metadata.product
        ? [task.metadata.product as ForecastChangedProduct]
        : [];
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [checkedProductIds, setCheckedProductIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedProductId(products[0]?.productId ?? null);
    setCheckedProductIds(new Set());
  }, [task?.id]);

  const selectedProduct =
    products.find((product) => product.productId === selectedProductId) ??
    products[0] ??
    null;
  const selectedIsChecked = selectedProduct
    ? checkedProductIds.has(selectedProduct.productId)
    : false;

  const handleMarkChecked = async () => {
    if (!selectedProduct) return;
    const index = products.findIndex(
      (product) => product.productId === selectedProduct.productId,
    );
    const taskId = task?.sourceIds[index] ?? task?.id;
    if (!taskId || selectedIsChecked) return;

    try {
      await onAcknowledge(taskId);
    } catch {
      return;
    }

    const nextChecked = new Set(checkedProductIds);
    nextChecked.add(selectedProduct.productId);
    setCheckedProductIds(nextChecked);

    const nextProduct = products.find(
      (product) =>
        product.productId !== selectedProduct.productId &&
        !nextChecked.has(product.productId),
    );
    if (nextProduct) {
      setSelectedProductId(nextProduct.productId);
      return;
    }

    onAllChecked?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isAcknowledging ? undefined : onClose}
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="md"
    >
      <DialogHeader>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {task?.title ?? 'Forecast has changed'}
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={isAcknowledging}
            sx={{ p: 0, color: colors.secondaryText }}
          >
            <Icon icon="X01" size={IconSize.LARGE} color="secondary" />
          </IconButton>
        </Stack>
      </DialogHeader>

      <Stack
        borderBottom={`1px solid ${colors.border}`}
        direction="row"
        p={4}
        gap={2}
        sx={{
          flexWrap: 'nowrap',
          overflowX: 'auto',
        }}>
        {products.map((product) => (
          <ProductCard
            key={product.productId}
            product={product}
            selected={product.productId === selectedProductId}
            checked={checkedProductIds.has(product.productId)}
            onSelect={setSelectedProductId}
          />
        ))}
      </Stack>


      <Stack sx={{ p: 4, gap: 4, backgroundColor: colors.backgroundTint }}>
        <ForecastCompareChart product={selectedProduct} />
      </Stack>

      <Box p={4} borderTop={`1px solid ${colors.border}`}>
        <ForecastChangedMetrics
          product={selectedProduct}
          isAcknowledging={isAcknowledging}
          alreadyChecked={selectedIsChecked}
          onMarkChecked={handleMarkChecked}
        />
      </Box>
    </Dialog>
  );
};

export default ForecastChangedDialog;
